import classNames from 'classnames';
import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { branch, defaultProps, lifecycle, renderComponent, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators, compose } from '../../../../../node_modules/redux';
import { contentSorting, routes, searchTileType } from '../../../../constants';
import { isManager, mapUserCpdCategoryProgress } from '../../../../services';
import { cpdCategorysActions } from '../../../../state/modules/areas-of-advice';
import { publisherActions } from '../../../../state/modules/publisher';
import { searchTileActions } from '../../../../state/modules/search-tile';
import { currentUserCpdDaysRemaining, currentUserSelector, userActions } from '../../../../state/modules/user';
import { FormField, BookmarkContentFilterGroup, SearchToggler, Icon } from '../../../components';
import Loading from '../../loading';
import ContentList, { searchTileFilter } from './partials/content-list';

const Bookmark = (props) => {
  return (
    <div className='base-layout'>
      <section id='header--training' className='layout--header'>
        <Grid>
          <Row className='no-gutter'>
            <Col xs={12} md={7}>
              <Breadcrumb>
                <BreadcrumbItem active>
                  Bookmark
                </BreadcrumbItem>
              </Breadcrumb>
              <h1 className='flexbox align-center'>
                <Icon iconSet='glyphicon' icon='bookmark' style={{ filter: 'invert(0.5) sepia(1) saturate(5) hue-rotate(123deg)' }} />
                Bookmarked Content
              </h1>
              <Row className='no-gutter'>
                <Col xs={12} md={6} className='no-padding top-gap flexbox nowrap'>
                  <span className='right-gap'>
                    <SearchToggler onChange={props.onSearchChange} />
                  </span>
                  <BookmarkContentFilterGroup {...{
                    onApply: props.updateFilters
                  }} />
                </Col>
                <Col xs={12} md={5} mdOffset={1} className='no-padding'>
                  <form className={classNames('searchToggle searchDesktop', { block: props.displaySearchBlock })}>
                    <FormField id='sort-by' label='Sort by' type='select-item' required>
                      <select
                        id='sort-by'
                        value={props.sort}
                        required
                        onChange={props.onSortByChange}>
                        {
                          fp.map((sortingKey) =>
                            <option key={sortingKey} value={sortingKey}>{fp.get(sortingKey, contentSorting).displayName}</option>
                          )(fp.keys(contentSorting))
                        }
                      </select>
                    </FormField>
                  </form>
                </Col>
              </Row>
            </Col>
          </Row>
        </Grid>
      </section>
      <ComposedContentSection {...props} />
    </div>
  );
}

const SearchResult = ({ filter }) => <ContentList filter={filter} />

const ComposedContentSection = compose(
  branch(
    ({ isFetchingCpdCategory, isFetchingUserCpdCategorys }) => isFetchingCpdCategory || isFetchingUserCpdCategorys,
    renderComponent(Loading)
  )
)(SearchResult);

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  cpdDaysRemaining: currentUserCpdDaysRemaining(state),
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  isFetchingCpdCategorys: state.cpdCategorys.isFetching,
  publishers: state.publisher.publishers,
  isFetchingPublishers: state.publisher.isFetching,
  userCpdCategoryProgress: state.user.cpdCategoryProgress,
  userCpdCategories: state.user.cpdCategories,
  isFetchingUserCpdCategorys: state.user.currentUserCpdCategorysIsFetching,
  isFetchingContent: state.searchTile.isFetching,
  industryCpdCategorys: state.industry.cpdCategorys,
  userState: state.user,
  goals: fp.get('user.profile.stat.goals')(state)
});

const mapDispatchToProps = (dispatch) => ({
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getSearchTiles: bindActionCreators(searchTileActions.searchTile.get, dispatch),
  getUserCpdCategorys: bindActionCreators(userActions.user.getCpdCategorys, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getUserBookmarks: bindActionCreators(userActions.user.getUserBookmarks, dispatch),
})

/**
 * Render a page for displaying user's bookmarked caddie content only.
 */
export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ currentUser }) => isManager(currentUser),
    renderComponent(() => (
      <Redirect to={routes.app.teams.root} />
    ))
  ),
  withStateHandlers(
    {
      initiatedContentLoading: false,
      displaySearchBlock: false,
      searchQuery: '',
      sort: fp.head(fp.keys(contentSorting)),
      searchDebounce: undefined,
      filters: {}
    },
    {
      updateFilters: () => (filters) => ({ filters }),
      updateSearchDebound: () => (searchDebounce) => ({ searchDebounce }),
      onSortByChange: () => (e) => ({
        sort: e.target.value
      }),
      onSearchChange: () => (query) => ({
        searchQuery: query,
      }),
      onSearchIconClick: ({ displaySearchBlock }) => () => ({
        displaySearchBlock: !displaySearchBlock
      }),
      contentLoadingInitiated: () => () => ({
        initiatedContentLoading: true
      })
    }
  ),
  withProps(
    ({
      searchQuery,
      sort,
      filters
    }) => {
      const cpdCategories = fp.join(',')(filters.cpdCategoryIds);
      const associations = fp.join(',')(filters.associations);
      const locations = fp.join(',')(filters.locations);
      const emptyUserCpdCategorys = null;
      const emptyPublishers = null;
      const emptyBusinessId = null;
      const emptyContentType = null;
      const filter = searchTileFilter(
        searchTileType.Content,
        cpdCategories,
        sort,
        emptyUserCpdCategorys,
        searchQuery,
        emptyPublishers,
        emptyBusinessId,
        associations,
        locations,
        filters.min,
        filters.max,
        emptyContentType,
        filters.isBookmarkedOnly,
      );
      return {
        filter: {
          ...filter,
          goals: fp.join(',')(filters.goalIds),
          isBookmarkedOnly: true,
        }
      };
    }
  ),
  defaultProps(
    {
      userCpdCategoryProgress: {}
    }
  ),
  withProps(
    ({ cpdCategorys, userCpdCategoryProgress, userState }) => ({
      mappedCpdCategorys: fp.filter(
        ({ hasChild }) => !hasChild
      )(mapUserCpdCategoryProgress(cpdCategorys)(userCpdCategoryProgress)),
      userCpdCategoryIds: userState.currentUserCpdCategorys,
      allCpdCategorys: fp.map((cpdCategory) => ({
        cpdCategory: cpdCategory.id,
        title: cpdCategory.name
      }))(cpdCategorys),
      overallCpdProgress: {
        totalCpdCategorys: fp.get('cpdCategorys.length')(userCpdCategoryProgress),
        ...fp.get('primaryStat')(userCpdCategoryProgress),
        ...fp.get('derivedStat')(userCpdCategoryProgress),
      }
    })
  ),
  lifecycle({
    componentDidMount() {
      const {
        cpdCategory,
        isFetchingCpdCategory,
        mappedCpdCategorys,
        publishers,
        isFetchingPublishers,
        getCpdCategorys,
        getPublishers,
        getUserCpdCategorys,
        getSearchTiles,
        filter,
        getUserBookmarks,
      } = this.props;

      getSearchTiles(0, filter);
      getUserBookmarks();

      if (fp.isEmpty(cpdCategory) && !isFetchingCpdCategory) {
        getCpdCategorys();
      }
      if (fp.isEmpty(publishers) && !isFetchingPublishers) {
        getPublishers();
      }
      if (fp.isEmpty(mappedCpdCategorys)) {
        getUserCpdCategorys();
      }
    },
    componentDidUpdate(prevProps) {
      const {
        getSearchTiles,
        filter,
        searchDebounce,
        updateSearchDebound
      } = this.props

      if (!fp.isEqual(filter)(prevProps.filter)) {
        const debounced = fp.debounce(500)(() => {
          getSearchTiles(0, filter);
        });
        searchDebounce && searchDebounce.cancel();
        updateSearchDebound(debounced)
        debounced();
      }
    }
  })
)(Bookmark)


// WEBPACK FOOTER //
// ./src/containers/pages/app/training/bookmark.js